import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { FaTimes, FaMoneyBillWave, FaCheckCircle } from "react-icons/fa";
import { saveBankDetails } from "../../features/wallet/walletSlice";

const UpdateBankDetailsModal = ({ isOpen, onClose, onSuccess, existingDetails }) => {
  const dispatch = useDispatch();
  const { isLoading } = useSelector((state) => state.wallet);

  const [accountName, setAccountName] = useState("");
  const [accountNumber, setAccountNumber] = useState("");
  const [ifscCode, setIfscCode] = useState("");
  const [bankName, setBankName] = useState("");
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (isOpen && existingDetails) {
      setAccountName(existingDetails.accountName || "");
      setAccountNumber(existingDetails.accountNumber || "");
      setIfscCode(existingDetails.ifscCode || "");
      setBankName(existingDetails.bankName || "");
    } else if (isOpen) {
      setAccountName("");
      setAccountNumber("");
      setIfscCode("");
      setBankName("");
    }
    setErrors({});
  }, [isOpen, existingDetails]);

  const validateForm = () => {
    const newErrors = {};

    if (!accountName.trim()) {
      newErrors.accountName = "Account holder name is required";
    }

    if (!accountNumber.trim() || accountNumber.length < 9) {
      newErrors.accountNumber = "Valid account number is required (min 9 digits)";
    }

    if (!ifscCode.trim() || ifscCode.length < 4) {
      newErrors.ifscCode = "Valid IFSC code is required";
    }

    if (!bankName.trim()) {
      newErrors.bankName = "Bank name is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    const bankDetailsPayload = {
      accountName,
      accountNumber,
      ifscCode: ifscCode.toUpperCase(),
      bankName,
    };

    const resultAction = await dispatch(saveBankDetails(bankDetailsPayload));

    if (saveBankDetails.fulfilled.match(resultAction)) {
      if (onSuccess) {
        onSuccess(resultAction.payload);
      }
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[9999] flex items-start justify-center p-3 sm:p-4 pt-20 sm:pt-24 overflow-y-auto">
      <div className="bg-white rounded-3xl shadow-2xl max-w-3xl w-full p-6 sm:p-7 relative max-h-[92vh] overflow-y-auto border border-slate-100">
        {/* Header */}
        <div className="flex justify-between items-center mb-5 pb-4 border-b border-slate-100">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-xl bg-orange-50 text-orange-600 flex items-center justify-center">
              <FaMoneyBillWave size={16} />
            </div>
            <div>
              <h2 className="text-xl font-bold text-slate-900 leading-tight">
                {existingDetails ? "Update Bank Account" : "Add Bank Account"}
              </h2>
              <p className="text-xs text-slate-500">Provide bank account for direct withdrawal transfers</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600 p-2 rounded-xl hover:bg-slate-100 transition-colors"
          >
            <FaTimes size={16} />
          </button>
        </div>

        {/* 2x2 Grid Form Fields */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-5">
          {/* Account Holder Name */}
          <div>
            <label className="block text-xs font-bold text-slate-700 uppercase tracking-wide mb-1.5">
              Account Holder Name
            </label>
            <input
              type="text"
              value={accountName}
              onChange={(e) => setAccountName(e.target.value)}
              placeholder="As per bank passbook"
              className={`w-full px-3.5 py-2.5 rounded-xl border text-sm font-medium focus:outline-none transition ${
                errors.accountName
                  ? "border-red-500 focus:ring-4 focus:ring-red-100"
                  : "border-slate-300 focus:border-orange-500 focus:ring-4 focus:ring-orange-500/10"
              }`}
            />
            {errors.accountName && (
              <p className="text-red-500 text-xs mt-1">{errors.accountName}</p>
            )}
          </div>

          {/* Bank Name */}
          <div>
            <label className="block text-xs font-bold text-slate-700 uppercase tracking-wide mb-1.5">
              Bank Name
            </label>
            <input
              type="text"
              value={bankName}
              onChange={(e) => setBankName(e.target.value)}
              placeholder="e.g. HDFC Bank, SBI, ICICI"
              className={`w-full px-3.5 py-2.5 rounded-xl border text-sm font-medium focus:outline-none transition ${
                errors.bankName
                  ? "border-red-500 focus:ring-4 focus:ring-red-100"
                  : "border-slate-300 focus:border-orange-500 focus:ring-4 focus:ring-orange-500/10"
              }`}
            />
            {errors.bankName && (
              <p className="text-red-500 text-xs mt-1">{errors.bankName}</p>
            )}
          </div>

          {/* Account Number */}
          <div>
            <label className="block text-xs font-bold text-slate-700 uppercase tracking-wide mb-1.5">
              Account Number
            </label>
            <input
              type="text"
              value={accountNumber}
              onChange={(e) => setAccountNumber(e.target.value)}
              placeholder="Enter bank account number"
              className={`w-full px-3.5 py-2.5 rounded-xl border text-sm font-mono focus:outline-none transition ${
                errors.accountNumber
                  ? "border-red-500 focus:ring-4 focus:ring-red-100"
                  : "border-slate-300 focus:border-orange-500 focus:ring-4 focus:ring-orange-500/10"
              }`}
            />
            {errors.accountNumber && (
              <p className="text-red-500 text-xs mt-1">{errors.accountNumber}</p>
            )}
          </div>

          {/* IFSC Code */}
          <div>
            <label className="block text-xs font-bold text-slate-700 uppercase tracking-wide mb-1.5">
              IFSC Code
            </label>
            <input
              type="text"
              value={ifscCode}
              onChange={(e) => setIfscCode(e.target.value.toUpperCase())}
              placeholder="e.g. HDFC0000001"
              className={`w-full px-3.5 py-2.5 rounded-xl border text-sm font-mono uppercase focus:outline-none transition ${
                errors.ifscCode
                  ? "border-red-500 focus:ring-4 focus:ring-red-100"
                  : "border-slate-300 focus:border-orange-500 focus:ring-4 focus:ring-orange-500/10"
              }`}
            />
            {errors.ifscCode && (
              <p className="text-red-500 text-xs mt-1">{errors.ifscCode}</p>
            )}
          </div>
        </div>

        {/* Info */}
        <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 mb-5 text-xs text-slate-600">
          <p className="font-bold text-slate-800 text-[11px] uppercase tracking-wide mb-1">Important:</p>
          <p className="text-slate-500">Ensure account number and IFSC code are accurate. This account will receive all future wallet withdrawal transfers.</p>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 bg-slate-100 text-slate-700 font-bold py-2.5 rounded-xl hover:bg-slate-200 transition text-xs"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={isLoading}
            className="flex-1 bg-orange-500 text-white font-bold py-2.5 rounded-xl hover:bg-orange-600 transition text-xs shadow-md shadow-orange-500/20 disabled:bg-slate-300 disabled:cursor-not-allowed"
          >
            {isLoading ? "Saving..." : existingDetails ? "Update Details" : "Save Account"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default UpdateBankDetailsModal;
